import { getRandomInt, shuffle } from "../Shared/Helpers.tsx";
import { BarChartSpec, BarGraphQuestionOptions, ProblemData, ProblemFormatOptions } from "./Constants.ts";
import { ISettingsProps } from "./Models.ts";
import { PrintedLanguage, printedLanguageOf } from "./PrintedLanguage.ts";
import { barGraphLabelSets as enSets, barGraphPrompts as en } from "../Files/Prompts/en/barGraphs.ts";
import { barGraphLabelSets as esSets, barGraphPrompts as es } from "../Files/Prompts/es/barGraphs.ts";
import { barGraphLabelSets as frSets, barGraphPrompts as fr } from "../Files/Prompts/fr/barGraphs.ts";

const PROMPTS: Record<PrintedLanguage, typeof en> = { en, es, fr };
const LABEL_SET_TABLE: Record<PrintedLanguage, typeof enSets> = { en: enSets, es: esSets, fr: frSets };

/**
 * One chart, several questions (ADR 0040). A bar-graph sheet used to draw a
 * fresh chart per problem, so a page of four questions drew four charts - a
 * student never compared two values on one chart, which is not how the topic
 * is taught. This file now builds *groups*: draw one chart, ask
 * `BarGraphQuestionsPerChart` distinct questions about it, tag every one with
 * the same `problemGroup` so `PdfGenerator/Page.tsx` draws the chart once
 * above the group's rows, then move on to a fresh chart for the next group.
 *
 * The chart itself is `Shared/Pdf/BarChart.tsx`; this file only decides what
 * to ask about it.
 *
 * The rule this file exists to enforce is ADR 0039 §3's: **every bar ends
 * exactly on a labelled gridline.** So every value is drawn as a multiple of
 * the grid step, never drawn freely and rounded - the same "answer-first"
 * preference ShapeGenerator states for its aspect-ratio bound. And **exactly
 * one bar is the largest**, for the reason AveragesGenerator draws exactly one
 * mode: a tie has two correct answers and a key can only print one.
 *
 * ADR 0040 §3 adds a second rule, local to one chart: **a group's questions
 * must be distinct in what they ask**, not merely in their numbers. `Total`
 * and `Largest` each have exactly one true answer, so a chart supports at
 * most one of each; two `Difference` questions are fine only about different
 * pairs, two `ReadOne` questions only about different categories. A single
 * chart can run out of new questions of a pinned type before the sheet's
 * `BarGraphQuestionsPerChart` quota is met - asking `Total` three times about
 * one chart has no third question to ask - so a group is allowed to come up
 * short and hand off to a fresh chart rather than repeat itself.
 */

/** The chart is always divided into this many steps, so every offered
 * BAR_GRAPH_MAX_OPTIONS value (10, 20, 50, 100) splits into a whole step. */
const GRID_DIVISIONS = 5;

/** How many bars a chart draws. */
const MIN_BARS = 4;
const MAX_BARS = 6;

/**
 * `count` category labels from one randomly chosen vocabulary, in the
 * worksheet's printed language (ADR 0051 §7) - the category names are drawn
 * on the chart and named in the prompt sentence, so they have to agree.
 * Months and days are read as a consecutive run - "Mar, Apr, May, Jun" reads
 * like a real chart; "Jan, Jun, Sep" does not - so those are sliced rather
 * than shuffled.
 */
function pickLabels(count: number, language: PrintedLanguage): string[] {
    const sets = LABEL_SET_TABLE[language];
    const set = sets[getRandomInt(0, sets.length - 1)];
    if (set.sequential) {
        const start = getRandomInt(0, Math.max(0, set.labels.length - count));
        return set.labels.slice(start, start + count);
    }
    return shuffle(set.labels).slice(0, count);
}

/**
 * `count` values, each a multiple of `gridStep`, with exactly one of them the
 * chart's maximum.
 *
 * Drawn from a shuffled, non-repeating pool of the step multiples
 * (1..GRID_DIVISIONS) - so as long as `count` fits inside the pool, every
 * value is distinct and the maximum is unique by construction, the same way
 * two coprime multipliers keep NumberPropertyGenerator's GCF from coming out
 * 1. Only when a chart needs more bars than the pool has multiples does a
 * value repeat, and the repeat is deliberately drawn from below the maximum
 * so it can never tie it.
 */
function barValues(count: number, gridStep: number): number[] {
    const pool = shuffle(Array.from({ length: GRID_DIVISIONS }, (_, i) => i + 1));
    const chosen = pool.slice(0, Math.min(count, pool.length));

    while (chosen.length < count) {
        const currentMax = Math.max(...chosen);
        const belowMax = chosen.filter((value) => value !== currentMax);
        chosen.push(belowMax[getRandomInt(0, belowMax.length - 1)]);
    }

    return chosen.map((step) => step * gridStep);
}

/** A freshly drawn chart: categories, values and the scale they are drawn at. */
function buildChart(
    maxValue: number,
    language: PrintedLanguage
): { bars: { label: string; value: number }[]; gridStep: number } {
    const gridStep = Math.max(1, Math.round(maxValue / GRID_DIVISIONS));
    const count = getRandomInt(MIN_BARS, MAX_BARS);
    const labels = pickLabels(count, language);
    const values = barValues(count, gridStep);
    const bars = labels.map((label, i) => ({ label, value: values[i] }));
    return { bars, gridStep };
}

function questionTypes(question: BarGraphQuestionOptions): BarGraphQuestionOptions[] {
    if (question === BarGraphQuestionOptions.Mixed) {
        return [
            BarGraphQuestionOptions.ReadOne,
            BarGraphQuestionOptions.Difference,
            BarGraphQuestionOptions.Total,
            BarGraphQuestionOptions.Largest,
        ];
    }
    return [question];
}

/** An unordered pair of labels, order-independent, for tracking which pairs a group has already asked about. */
function pairKeyFor(a: string, b: string): string {
    return [a, b].sort().join('\u0000');
}

/**
 * A distinct-value bar pair whose (unordered) label pair is not in
 * `excludePairs`, chosen from every candidate rather than a few random
 * attempts - the group sizes here are small (at most four), so enumerating
 * every pair costs nothing and lets a group tell reliably when it has run out
 * of new pairs to ask about. `null` means every pair is either equal-valued
 * or already asked.
 */
function differingPair(
    bars: { label: string; value: number }[],
    excludePairs: Set<string>
): [number, number] | null {
    const candidates: [number, number][] = [];
    for (let i = 0; i < bars.length; i++) {
        for (let j = i + 1; j < bars.length; j++) {
            if (bars[i].value === bars[j].value) continue;
            if (excludePairs.has(pairKeyFor(bars[i].label, bars[j].label))) continue;
            candidates.push([i, j]);
        }
    }
    if (candidates.length === 0) return null;
    return candidates[getRandomInt(0, candidates.length - 1)];
}

/** What a group has already asked, so the next question stays distinct (ADR 0040 §3). */
interface GroupUsage {
    totalAsked: boolean;
    largestAsked: boolean;
    pairsAsked: Set<string>;
    categoriesAsked: Set<string>;
}

function newGroupUsage(): GroupUsage {
    return { totalAsked: false, largestAsked: false, pairsAsked: new Set(), categoriesAsked: new Set() };
}

/** One built question, or null when this chart has nothing new left of this type to ask. */
function tryBuildQuestion(
    type: BarGraphQuestionOptions,
    bars: { label: string; value: number }[],
    used: GroupUsage,
    language: PrintedLanguage
): { type: BarGraphQuestionOptions; prompt: string; answer: string; mark: () => void } | null {
    const t = PROMPTS[language];
    switch (type) {
        case BarGraphQuestionOptions.Total: {
            if (used.totalAsked) return null;
            const total = bars.reduce((sum, bar) => sum + bar.value, 0);
            return {
                type,
                prompt: t.total(),
                answer: String(total),
                mark: () => { used.totalAsked = true; },
            };
        }
        case BarGraphQuestionOptions.Largest: {
            if (used.largestAsked) return null;
            const highest = Math.max(...bars.map((bar) => bar.value));
            const winner = bars.find((bar) => bar.value === highest)!;
            return {
                type,
                prompt: t.largest(),
                answer: winner.label,
                mark: () => { used.largestAsked = true; },
            };
        }
        case BarGraphQuestionOptions.Difference: {
            const pair = differingPair(bars, used.pairsAsked);
            if (!pair) return null;
            const [i, j] = pair;
            const [smaller, larger] =
                bars[i].value <= bars[j].value ? [bars[i], bars[j]] : [bars[j], bars[i]];
            const pairKey = pairKeyFor(bars[i].label, bars[j].label);
            return {
                type,
                prompt: t.difference(larger.label, smaller.label),
                answer: String(larger.value - smaller.value),
                mark: () => { used.pairsAsked.add(pairKey); },
            };
        }
        default: {
            // ReadOne, and Mixed's fallback (NumberPropertyGenerator's buildProblem
            // uses the same pattern for its own default case).
            const available = bars.filter((bar) => !used.categoriesAsked.has(bar.label));
            if (available.length === 0) return null;
            const bar = available[getRandomInt(0, available.length - 1)];
            return {
                type: BarGraphQuestionOptions.ReadOne,
                prompt: t.readOne(bar.label),
                answer: String(bar.value),
                mark: () => { used.categoriesAsked.add(bar.label); },
            };
        }
    }
}

/**
 * One group: a freshly drawn chart and up to `want` distinct questions about
 * it, all tagged with the same `problemGroup` so the page draws the chart
 * once (ADR 0040 §1, §2). Comes back shorter than `want` when the allowed
 * question types run out of new things to ask about this particular chart
 * (a pinned `Total` or `Largest` sheet can only ever ask one question per
 * chart) - the caller draws a fresh chart for the remainder rather than this
 * function repeating a question.
 *
 * Always returns at least one problem: a brand-new chart's usage record is
 * empty, and every question type can build at least one question against an
 * unused chart.
 *
 * `startTypeIndex` is where the rotation through `allowedTypes` begins, and
 * the returned `nextTypeIndex` is where it left off. On a Mixed sheet with
 * fewer questions per chart than there are question types, every group's
 * *own* usage record is fresh - a new chart has never asked `Total` - but
 * without carrying the rotation forward, every group would restart at index
 * 0 and a `BarGraphQuestionsPerChart` of 2 would ask ReadOne and Difference
 * on every single chart, never Total or Largest, which is not what "Mixed"
 * promises. Threading the index through `generateBarGraphPages` is what
 * makes a two-per-chart Mixed sheet actually cycle through all four.
 */
export function buildBarGraphGroup(
    allowedTypes: BarGraphQuestionOptions[],
    maxValue: number,
    want: number,
    groupKey: string,
    startKey: number,
    startTypeIndex = 0,
    language: PrintedLanguage = 'en'
): { problems: ProblemData[]; nextKey: number; nextTypeIndex: number } {
    const safeMax = Math.max(GRID_DIVISIONS, Math.floor(maxValue) || 20);
    const { bars, gridStep } = buildChart(safeMax, language);
    const chart: BarChartSpec = { bars, maxValue: safeMax, gridStep };
    const used = newGroupUsage();
    const problems: ProblemData[] = [];
    let key = startKey;
    let typeIndex = startTypeIndex;
    let stalled = 0;

    while (problems.length < want && stalled < allowedTypes.length) {
        const type = allowedTypes[typeIndex % allowedTypes.length];
        typeIndex++;
        const picked = tryBuildQuestion(type, bars, used, language);
        if (!picked) {
            stalled++;
            continue;
        }
        stalled = 0;
        picked.mark();
        problems.push({
            key: key++,
            numbers: [],
            operator: "",
            display: [picked.prompt, picked.answer],
            barGraphQuestion: picked.type,
            problemGroup: { key: groupKey, format: ProblemFormatOptions.BarGraphs, barChart: chart },
        });
    }

    return { problems, nextKey: key, nextTypeIndex: typeIndex % allowedTypes.length };
}

/** A page-shaped set of bar-graph problems, in groups of `BarGraphQuestionsPerChart`. */
export function generateBarGraphPages(settings: ISettingsProps): ProblemData[][] | null {
    const types = questionTypes(settings.BarGraphQuestion ?? BarGraphQuestionOptions.Mixed);
    const maxValue = settings.BarGraphMaxValue ?? 20;
    const perChart = Math.max(1, settings.BarGraphQuestionsPerChart ?? 3);
    const perPage = settings.NumberOfProblemsPerPage;
    const language = printedLanguageOf(settings);

    const pages: ProblemData[][] = [];
    let nextKey = 1;

    for (let page = 0; page < settings.NumberOfPages; page++) {
        const problems: ProblemData[] = [];
        let groupIndex = 0;
        // Carries the Mixed rotation across groups within this page, so a
        // BarGraphQuestionsPerChart lower than the number of question types
        // still cycles through all of them over several charts rather than
        // every chart asking the same leading types. See buildBarGraphGroup's
        // doc comment.
        let typeIndex = 0;
        // A group always yields at least one problem against a fresh chart, so
        // this makes progress every pass; the guard is only there so a future
        // change to that guarantee fails loudly instead of hanging the tab.
        const guardLimit = perPage * 4 + 10;
        let guard = 0;

        while (problems.length < perPage && guard < guardLimit) {
            guard++;
            const want = Math.min(perChart, perPage - problems.length);
            const { problems: groupProblems, nextKey: after, nextTypeIndex } = buildBarGraphGroup(
                types,
                maxValue,
                want,
                `bar-graph:${page}:${groupIndex}`,
                nextKey,
                typeIndex,
                language
            );
            if (groupProblems.length === 0) {
                break;
            }
            nextKey = after;
            typeIndex = nextTypeIndex;
            problems.push(...groupProblems);
            groupIndex++;
        }

        pages.push(problems);
    }

    return pages.some((page) => page.length > 0) ? pages : null;
}
